import React, { useState, useRef } from "react";
import { QueryBreakdownData, dpAwareCount } from "../QueryBreakdownModal";
import {
  CHART_COLORS,
  IX_COLOR,
  hexToRgba,
  formatTick,
  getYAxis,
} from "../audienceChartUtils";
import { useBarHoverTooltip } from "../../hooks/use-bar-hover-tooltip";
import InforIcon from "../../assets/InfoCircleIcon";
import styles from "./audienceBreakdownCard.module.css";
import { OI } from "../../onam/mark";
import { FEATURES } from "../../onam/features";

const MINI_H = 140;

interface AudienceBreakdownCardProps {
  data: QueryBreakdownData;
  createdAt?: string;
  onExpand: () => void;
  previousRuns?: Array<{ data: QueryBreakdownData; createdAt?: string }>;
  onExpandRun?: (data: QueryBreakdownData) => void;
  /** Largest count across all cards in the current grid, so bar heights are
   * comparable across cards instead of each card scaling to its own max. */
  globalMaxCount?: number;
}

const AudienceBreakdownCard: React.FC<AudienceBreakdownCardProps> = ({
  data,
  createdAt,
  onExpand,
  previousRuns = [],
  onExpandRun,
  globalMaxCount,
}) => {
  const { queryId, queryName, campaignName, intersectionCount, executedQueries } = data;
  const datasetCount = executedQueries.length;
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const chartBodyRef = useRef<HTMLDivElement>(null);
  const {
    hoveredIdx,
    tooltipColor,
    tooltipStyle,
    tooltipFlipped,
    tooltipRef,
    showTooltip,
    hideTooltip,
  } = useBarHoverTooltip(chartBodyRef);

  const runCount = previousRuns.length + 1;

  const normalizeOperator = (op: string): string => {
    if (op === "eq") return "=";
    if (op === "neq" || op === "ne") return "≠";
    if (op === "gt") return ">";
    if (op === "gte" || op === "ge") return "≥";
    if (op === "lt") return "<";
    if (op === "lte" || op === "le") return "≤";
    return op;
  };

  const hasFiltersApplied = executedQueries.some((q) =>
    q.filters_applied?.some((f: any) => f.status === "applied"),
  );

  const globalFilterChips: string[] = hasFiltersApplied
    ? Array.from(
        new Set(
          executedQueries.flatMap((q) =>
            (q.filters_applied || [])
              .filter((f: any) => f.status === "applied")
              .flatMap((f: any) =>
                f.conditions
                  .filter((c: any) => c.operator !== "logical")
                  .map(
                    (c: any) =>
                      `${f.attribute} ${normalizeOperator(c.operator)} ${c.value}`,
                  ),
              ),
          ),
        ),
      )
    : [];

  const renderTooltipContent = () => {
    if (hoveredIdx === null) return null;
    if (hoveredIdx === datasetCount) {
      return (
        <>
          <div className={styles.ttOrg}>∩ Overlap</div>
          <div className={styles.ttCount}>
            {intersectionCount.toLocaleString("en-US")}
            <span className={styles.ttOf}>matched</span>
          </div>
        </>
      );
    }
    const ds = executedQueries[hoveredIdx];
    if (!ds) return null;
    const matched = dpAwareCount(ds);
    const appliedFilters = (ds.filters_applied || []).filter(
      (f: any) => f.status === "applied",
    );
    const hasFiltersData = (ds.filters_applied || []).length > 0;
    const chips: string[] =
      appliedFilters.length > 0
        ? appliedFilters.flatMap((f: any) =>
            f.conditions
              .filter((c: any) => c.operator !== "logical")
              .map(
                (c: any) =>
                  `${f.attribute} ${normalizeOperator(c.operator)} ${c.value}`,
              ),
          )
        : hasFiltersData
          ? []
          : globalFilterChips;
    return (
      <>
        <div className={styles.ttOrg}>{ds.contributor_organization_name}</div>
        {ds.contributor_bucket_name && (
          <div className={styles.ttDs}>{ds.contributor_bucket_name}</div>
        )}
        <div className={styles.ttCount}>
          {matched.toLocaleString("en-US")}
          <span className={styles.ttOf}>matched</span>
        </div>
        {chips.length > 0 && (
          <>
            <div className={styles.ttDivider} />
            <div className={styles.ttChipsLabel}>Applied filters</div>
            <div className={styles.ttChips}>
              {chips.map((chip, i) => (
                <span key={i} className={styles.ttChip}>
                  <span className={styles.ttChipCheck}>{(FEATURES.curationSkin ? <OI n="check" size="1em" /> : "✓")}</span>
                  {chip}
                </span>
              ))}
            </div>
          </>
        )}
      </>
    );
  };

  const displayName = campaignName || queryName;
  const dateStr = createdAt
    ? new Date(createdAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    : null;

  const maxCount = Math.max(
    ...executedQueries.map((q) => dpAwareCount(q)),
    intersectionCount,
    1,
  );
  const scaleBasis = globalMaxCount ? Math.max(globalMaxCount, maxCount) : maxCount;
  const { yMax, ticks } = getYAxis(scaleBasis * 1.15);
  const barH = (count: number) => Math.max(2, (count / yMax) * MINI_H);
  const tickTopPx = (val: number) =>
    `${((1 - val / yMax) * MINI_H).toFixed(1)}px`;

  if (datasetCount === 0) {
    return (
      <div className={styles.cardWrapper}>
        <div className={styles.card}>
          <div className={styles.emptyMini}>
            <span className={styles.emptyIcon}>∅</span>
            <span className={styles.emptyMsg}>No contributor data</span>
          </div>
          <CardFooter
            displayName={displayName}
            intersectionCount={intersectionCount}
            dateStr={dateStr}
            onExpand={onExpand}
            runCount={runCount}
            isHistoryOpen={isHistoryOpen}
            onToggleHistory={() => setIsHistoryOpen((v) => !v)}
          />
        </div>
        {isHistoryOpen && previousRuns.length > 0 && (
          <HistoryPanel runs={previousRuns} onExpandRun={onExpandRun} />
        )}
      </div>
    );
  }

  return (
    <div className={styles.cardWrapper}>
      {/* Tooltip — fixed-position so it escapes the card's overflow:hidden */}
      <div
        ref={tooltipRef}
        role="tooltip"
        data-testid="audience-breakdown-chart-tooltip"
        className={`${styles.chartTooltip}${tooltipFlipped ? ` ${styles.chartTooltipFlipped}` : ""}`}
        style={{
          ...tooltipStyle,
          opacity: hoveredIdx !== null ? 1 : 0,
          borderLeft: `4px solid ${tooltipColor}`,
        }}
      >
        {renderTooltipContent()}
      </div>

      <div className={styles.card}>
        {/* Mini chart */}
        <div className={styles.chartWrap}>
          {/* Y-axis */}
          <div className={styles.yCol} aria-hidden="true">
            {[...ticks].reverse().map((tick) => (
              <span
                key={tick}
                className={styles.yLabel}
                style={{ top: tickTopPx(tick) }}
              >
                {formatTick(tick)}
              </span>
            ))}
          </div>

          {/* Bars + x-axis */}
          <div className={styles.chartBody} ref={chartBodyRef}>
            <div className={styles.barsArea}>
              {ticks.map((tick) => (
                <div
                  key={tick}
                  className={`${styles.gl}${tick === ticks[ticks.length - 1] ? ` ${styles.glCap}` : ""}`}
                  style={{ top: tickTopPx(tick) }}
                />
              ))}

              <div className={styles.barsFlex} key={queryId}>
                {executedQueries.map((ds, i) => {
                  const count = dpAwareCount(ds);
                  const color = CHART_COLORS[i % CHART_COLORS.length];
                  const hNum = barH(count);
                  const isDimmed = hoveredIdx !== null && hoveredIdx !== i;
                  const capacityH = ds.contributor_record_count
                    ? Math.min(
                        MINI_H,
                        Math.max(
                          2,
                          (ds.contributor_record_count / yMax) * MINI_H,
                        ),
                      )
                    : null;

                  return (
                    <div
                      key={i}
                      className={styles.barCol}
                      onMouseEnter={(e) => {
                        const el =
                          e.currentTarget.querySelector<HTMLDivElement>(
                            `.${styles.bar}`,
                          ) || e.currentTarget;
                        showTooltip(el, i, color);
                      }}
                      onMouseLeave={hideTooltip}
                    >
                      {capacityH !== null && (
                        <div
                          className={styles.barCapacity}
                          style={{
                            height: `${capacityH}px`,
                            background: hexToRgba(color, 0.14),
                          }}
                        />
                      )}
                      <div
                        className={styles.bar}
                        style={
                          {
                            height: `${hNum}px`,
                            "--d": `${60 + i * 70}ms`,
                            background: color,
                            opacity: isDimmed ? 0.22 : 1,
                          } as React.CSSProperties
                        }
                      />
                    </div>
                  );
                })}

                {datasetCount > 1 && (
                  <>
                    <div className={styles.ixDivider} aria-hidden="true" />
                    <div
                      className={styles.barCol}
                      onMouseEnter={(e) => {
                        if (intersectionCount === 0) return;
                        const el =
                          e.currentTarget.querySelector<HTMLDivElement>(
                            `.${styles.bar}`,
                          ) || e.currentTarget;
                        showTooltip(el, datasetCount, IX_COLOR);
                      }}
                      onMouseLeave={() => {
                        if (intersectionCount === 0) return;
                        hideTooltip();
                      }}
                    >
                      {intersectionCount === 0 ? (
                        <div className={styles.zeroIx}>
                          <span>∅</span>
                        </div>
                      ) : (
                        <div
                          className={`${styles.bar} ${styles.iBar}`}
                          style={
                            {
                              height: `${barH(intersectionCount)}px`,
                              "--d": `${60 + datasetCount * 70 + 70}ms`,
                              opacity:
                                hoveredIdx !== null && hoveredIdx !== datasetCount
                                  ? 0.22
                                  : 1,
                            } as React.CSSProperties
                          }
                        />
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* X-axis org names */}
            <div className={styles.xRow} aria-hidden="true">
              {executedQueries.map((ds, i) => (
                <div key={i} className={styles.xItem}>
                  <span
                    className={styles.xOrg}
                    style={{ color: CHART_COLORS[i % CHART_COLORS.length] }}
                    title={ds.contributor_organization_name}
                  >
                    {ds.contributor_organization_name}
                  </span>
                </div>
              ))}
              {datasetCount > 1 && (
                <>
                  <div className={styles.xIxGap} />
                  <div className={`${styles.xItem} ${styles.xItemIx}`}>
                    <span className={styles.xOrg} style={{ color: IX_COLOR }}>
                      ∩
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <CardFooter
          displayName={displayName}
          intersectionCount={intersectionCount}
          dateStr={dateStr}
          onExpand={onExpand}
          runCount={runCount}
          isHistoryOpen={isHistoryOpen}
          onToggleHistory={() => setIsHistoryOpen((v) => !v)}
        />
      </div>

      {isHistoryOpen && previousRuns.length > 0 && (
        <HistoryPanel runs={previousRuns} onExpandRun={onExpandRun} />
      )}
    </div>
  );
};

// ── Card footer ──────────────────────────────────────────────────────────────

interface CardFooterProps {
  displayName: string;
  intersectionCount: number;
  dateStr: string | null;
  onExpand: () => void;
  runCount: number;
  isHistoryOpen: boolean;
  onToggleHistory: () => void;
}

const CardFooter: React.FC<CardFooterProps> = ({
  displayName,
  intersectionCount,
  dateStr,
  onExpand,
  runCount,
  isHistoryOpen,
  onToggleHistory,
}) => (
  <div className={styles.footer}>
    <div className={styles.footerLeft}>
      <span className={styles.name} title={displayName}>
        {displayName}
      </span>
      <div className={styles.meta}>
        <span className={styles.reachBadge}>
          Matched: {intersectionCount.toLocaleString("en-US")}
        </span>
        {dateStr && (
          <>
            <span className={styles.metaDot}>·</span>
            <span className={styles.metaDate}>{dateStr}</span>
          </>
        )}
      </div>
    </div>
    <div className={styles.footerRight}>
      {runCount > 1 && (
        <>
          <button
            className={`${styles.runsChip}${isHistoryOpen ? ` ${styles.runsChipActive}` : ""}`}
            onClick={onToggleHistory}
            title={`${runCount} runs — ${isHistoryOpen ? "hide" : "show"} history`}
          >
            {runCount} runs
            {FEATURES.curationSkin ? <OI n="down" size={12} style={{ transform: isHistoryOpen ? "rotate(180deg)" : undefined, transition: "transform 0.2s" }} /> : (
<svg
              width="8"
              height="8"
              viewBox="0 0 8 8"
              fill="none"
              style={{
                transform: isHistoryOpen ? "rotate(180deg)" : undefined,
                transition: "transform 0.2s",
              }}
            >
              <path
                d="M1 2.5L4 5.5L7 2.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
)}
          </button>
          <span
            className={styles.tipWrap}
            data-tip="The chart shows the most recent run. Expand to view earlier runs."
          >
            <InforIcon className={styles.tipIcon} />
          </span>
        </>
      )}
      <button
        className={styles.expandBtn}
        onClick={onExpand}
        aria-label="View full breakdown"
        title="View full breakdown"
      >
        {FEATURES.curationSkin ? <OI n="expand" size={14} /> : (
<svg
          width="13"
          height="13"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2 12L12 2M12 2H6M12 2V8"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
)}
      </button>
    </div>
  </div>
);

// ── History panel ────────────────────────────────────────────────────────────

interface HistoryPanelProps {
  runs: Array<{ data: QueryBreakdownData; createdAt?: string }>;
  onExpandRun?: (data: QueryBreakdownData) => void;
}

const HistoryPanel: React.FC<HistoryPanelProps> = ({ runs, onExpandRun }) => (
  <div className={styles.historyPanel}>
    <div className={styles.historyHeader}>Previous runs</div>
    {runs.map((run, i) => {
      const runDateStr = run.createdAt
        ? new Date(run.createdAt).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })
        : "—";
      return (
        <div key={i} className={styles.historyRow}>
          <span className={styles.historyDate}>{runDateStr}</span>
          <span className={styles.historyReach}>
            Matched: {run.data.intersectionCount.toLocaleString("en-US")}
          </span>
          {onExpandRun && (
            <button
              className={styles.historyExpandBtn}
              onClick={() => onExpandRun(run.data)}
              title="View full breakdown for this run"
            >
              {FEATURES.curationSkin ? <OI n="expand" size={12} /> : (
<svg
                width="11"
                height="11"
                viewBox="0 0 14 14"
                fill="none"
              >
                <path
                  d="M2 12L12 2M12 2H6M12 2V8"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
)}
            </button>
          )}
        </div>
      );
    })}
  </div>
);

export default AudienceBreakdownCard;
